import React, { FC } from 'react'
import { Switch, Route, Redirect } from 'react-router-dom'
import { useParams } from 'react-router'
import { t } from '@lingui/macro'
import { AnalyticsNavPanel } from './AnalyticsNavPanel'
import Workbooks from './workbooks/Workbooks'
import Pathways from './pathways/Pathways'
import Search from './search/Search'
import Resources from './resources/Resources'
import People from './people/People'
import Certifications from './certifications/Certifications'
import UserDetail from './user-detail/UserDetailAnalytics'
import ResourceDetail from './resource-detail/ResourceDetailAnalytics'
import WorkbooksDetails from './workbooks-detail/WorkbooksDetail'
import HeadTitle from '../../components/common/head/HeadTitle'
import { staticLinks } from 'shared/linkBuilder'
import CertificationDetail from './certification-detail/CertificationDetail'
import CertificationEnrolmentAnalytics from './certification-enrolment/CertificationEnrolmentAnalytics'
import { useAnalyticsNavPanelQuery } from './AnalyticsNavPanel.codegen'
import PathwayEnrolmentDetailAnalytics from './pathways-detail/PathwayEnrolmentDetailAnalytics'
import { Media } from './media/Media'
import { MediaDetailAnalytics } from './media-detail/MediaDetailAnalytics'
import './AnalyticsPage.less'
import { useFeatureFlags } from '../../app/hooks/useFeatureFlags'
import { WithAnalyticsContext } from './analyticsFilters/WithAnalyticsContext'
import { usePublicApiSession } from 'front-end/src/public-api/hooks/usePublicApiSession'

export const AnalyticsPage: FC = () => {
  const { userIsAdminOrManager } = usePublicApiSession()
  const { analyticsSection } = useParams<{ analyticsSection: string }>()
  const { featureFlags } = useFeatureFlags()

  const basePath = '/analytics'

  const { data } = useAnalyticsNavPanelQuery({
    fetchPolicy: 'cache-first',
    skip: userIsAdminOrManager
  })

  const isShowCertifications =
    !!featureFlags?.certificates &&
    (userIsAdminOrManager || data?.me.isAdminOfSomeGroup)

  return (
    <WithAnalyticsContext>
      <HeadTitle title={t`Analytics`} />
      <div className={`panel Analytics ${analyticsSection}`}>
        <AnalyticsNavPanel />
        <div className="panel-content">
          <div className="AnalyticsPage">
            <Switch>
              <Route
                exact
                path={staticLinks.analyticsResources}
                render={() => <Resources showFilterPanel />}
              />
              <Route
                exact
                path={staticLinks.analyticsPeople}
                render={() => <People showFilterPanel />}
              />
              <Route
                exact
                path={staticLinks.analyticsWorkbooks}
                render={() => <Workbooks showFilterPanel />}
              />
              <Route
                path={`${staticLinks.analyticsWorkbooks}/:userTopicId/views`}
                render={() => <WorkbooksDetails />}
              />
              <Route
                path={`${staticLinks.analyticsPathways}/enrolment/:enrolmentId`}
                render={() => <PathwayEnrolmentDetailAnalytics />}
              />
              <Route
                path={`${staticLinks.analyticsResources}/:topicId`}
                render={() => <ResourceDetail />}
              />

              {userIsAdminOrManager && (
                <Route
                  exact
                  path={staticLinks.analyticsMedia}
                  render={() => <Media />}
                />
              )}

              <Route
                path={`${staticLinks.analyticsPeople}/:userId`}
                render={() => <UserDetail />}
              />

              {userIsAdminOrManager && (
                <Route
                  path={`${staticLinks.analyticsMedia}/:topicElementId`}
                  render={() => <MediaDetailAnalytics />}
                />
              )}

              {userIsAdminOrManager && (
                <Route
                  path={staticLinks.analyticsSearch}
                  render={() => <Search />}
                />
              )}

              {(userIsAdminOrManager || data?.me.isAdminOfSomeGroup) && (
                <Route
                  exact
                  path={staticLinks.analyticsPathways}
                  render={() => <Pathways showFilterPanel />}
                />
              )}

              {isShowCertifications && (
                <>
                  <Route
                    exact
                    path={staticLinks.analyticsCertifications}
                    render={() => <Certifications showFilterPanel />}
                  />

                  <Route
                    exact
                    path={`${staticLinks.analyticsCertifications}/:certificationId`}
                    render={() => <CertificationDetail showFilterPanel />}
                  />

                  <Route
                    exact
                    path={`${staticLinks.analyticsCertifications}/:certificationId/enrolment/:enrolmentId`}
                    render={() => <CertificationEnrolmentAnalytics />}
                  />
                </>
              )}
              <Route render={() => <Redirect to={`${basePath}/people`} />} />
            </Switch>
          </div>
        </div>
      </div>
    </WithAnalyticsContext>
  )
}
